// EmailPasswordForm.tsx
import React, { useEffect, useState } from 'react';
import { TextField, Button, InputAdornment, IconButton } from '@mui/material';
import { ButtonWrapper, ErrorText, FormWrapper, LoaderWrapper } from 'src/pages/ManualLookup/LoginSimulator/style';
import { useSendRecord } from 'src/hooks/useSendRecord';
import { LandingState } from 'src/pages/LoginSimulatorLanding';
import { useTrackEnrollmentMutation } from 'src/redux/services/notifications';
import LoadingScreen from 'src/frontend-common/components/LoadingScreen';
import { v4 as uuidv4 } from 'uuid';
import { usePostSimulatorResultMutation } from 'src/redux/services/login';
import { LabelText } from 'src/pages/LoginSimulatorLanding/style';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

interface FormData {
  username: string;
  password: string;
}

// Funzione per aggiungere "TEST-" alla stringa
export function prependTest(str: string): string {
  return `TEST-${str}`;
}

// Funzione per rimuovere "TEST-" dalla stringa se presente
function removeTestPrefix(str: string): string {
  const prefix = 'TEST-';
  if (str.startsWith(prefix)) {
    return str.slice(prefix.length);
  }
  return str; // Ritorna la stringa originale se non ha il prefisso "TEST-"
}

export const sendRecordWaitingTime = 5 * 1000;

interface ErrorType {
  type: 'email' | 'password';
  message: string;
}

const FakeLoginForm: React.FC<{
  frontEndAppKey?: string;
  backEndAppKey?: string;
  currentStep?: number;
  setCurrentStep?: React.Dispatch<React.SetStateAction<number>>;
  backButton?: boolean;
  setLandingState?: React.Dispatch<React.SetStateAction<LandingState>>;
  setUserData?: React.Dispatch<
    React.SetStateAction<{
      username: string;
      pw: string;
    }>
  >;
  username?: string;
  pw?: string;
  setFormState?: React.Dispatch<React.SetStateAction<LandingState>>;
  setIdentityId?: React.Dispatch<React.SetStateAction<string>>;
  showLabel?: boolean;
}> = ({
  frontEndAppKey,
  backEndAppKey,
  currentStep,
  setCurrentStep,
  backButton,
  setLandingState,
  setUserData,
  username: userNameFromParent,
  pw: pwFromParent,
  setFormState,
  setIdentityId,
  showLabel,
}) => {
  const [formData, setFormData] = useState<FormData>({
    username: '',
    password: '',
  });
  const [prevFormData, setPrevFormData] = useState<FormData>({
    username: userNameFromParent || '',
    password: pwFromParent || '',
  });
  const [sessionId, setSessionId] = useState<string>('');
  const [errors, setErrors] = useState<ErrorType[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const { sendRecord, error: errorSendRecord, isSuccess: isSuccessSendRecord } = useSendRecord();

  const [trackEnrollment, { isSuccess: isSuccessTrackEnrollment, error: errorTrackEnrollment, data: trackEnrollmentData }] = useTrackEnrollmentMutation();

  const [postSimulatorResult, { isSuccess: postSimulatorResultIsSuccess, error: postSimulatorResultError, data: postSimulatorResultData }] =
    usePostSimulatorResultMutation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const isNotFirstStep = !!(currentStep && currentStep > 0);

  const handleSubmit = async (e: React.FormEvent) => {
    setIsLoading(true);
    e.preventDefault();
    const _errors: ErrorType[] = [];
    if (!formData.username || formData.username.trim() === '') {
      _errors.push({ type: 'email', message: 'Username is required' });
    }

    if (prevFormData.username && formData.username !== removeTestPrefix(prevFormData.username)) {
      _errors.push({ type: 'email', message: 'Username does not match' });
    }

    if (!formData.password || formData.password.trim() === '') {
      _errors.push({ type: 'password', message: 'Password is required' });
    }

    if (prevFormData.password && formData.password !== prevFormData.password) {
      _errors.push({ type: 'password', message: 'Password does not match' });
    }

    // Pulisce eventuali errori
    setErrors(_errors);
    if (_errors.length === 0 && frontEndAppKey) {
      const _sessionId = uuidv4();
      setSessionId(_sessionId);
      await sendRecord(prependTest(formData.username), frontEndAppKey, _sessionId);
      setPrevFormData(formData);
    }
  };

  const handleBack = () => {
    if (setCurrentStep) {
      setCurrentStep((prev) => prev - 1);
    }
    if (currentStep === 1) {
      setPrevFormData({
        username: '',
        password: '',
      });
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  useEffect(() => {
    // Se il record è stato inviato con successo e non è il primo step allora aspetta 4 secondi e chiama la funzione di track enrollment
    if (isSuccessSendRecord && !isNotFirstStep && backEndAppKey) {
      setTimeout(() => {
        trackEnrollment({ sessionId, appKey: backEndAppKey || '' });
      }, sendRecordWaitingTime);
    }
    // Se il record è stato inviato con successo e non è il primo step allora aspetta 4 secondi e chiama la funzione di post simulator result
    if (isSuccessSendRecord && isNotFirstStep && frontEndAppKey) {
      setTimeout(() => {
        postSimulatorResult({ session_id: sessionId, app_key: frontEndAppKey });
      }, sendRecordWaitingTime);
    }
  }, [isSuccessSendRecord]);

  useEffect(() => {
    // Se il track enrollment è stato inviato con successo allora incrementa il current step
    if (isSuccessTrackEnrollment && setCurrentStep) {
      setCurrentStep((prev) => prev + 1);
      if (setUserData) {
        setUserData({ username: formData.username, pw: formData.password });
      }
      setFormData({ username: '', password: '' });
      if (setIdentityId) {
        setIdentityId(trackEnrollmentData?.identity_id || '');
      }
    }
    setIsLoading(false);
  }, [isSuccessTrackEnrollment]);

  const getFormStateFromScoreCluster = (scoreCluster: string) => {
    switch (scoreCluster) {
      case 'very_high':
      case 'high':
        return 'success';
      case 'medium':
        return 'warning';
      case 'low':
      case 'very_low':
        return 'fail';
      default:
        return 'fail';
    }
  };

  useEffect(() => {
    // Se il post simulator result è stato inviato con successo allora incrementa il current step
    if (postSimulatorResultIsSuccess) {
      if (postSimulatorResultData) {
        const _formState = getFormStateFromScoreCluster(postSimulatorResultData.score_cluster);
        if (setLandingState) {
          setLandingState(_formState);
        }
        if (setFormState) {
          setFormState(_formState);
        }
      }
      if (setCurrentStep) {
        setCurrentStep((prev) => prev + 1);
      }
    }
    setIsLoading(false);
  }, [postSimulatorResultIsSuccess]);

  const isError = errorSendRecord || errorTrackEnrollment || postSimulatorResultError || errors.length > 0;

  const showLoader = isLoading && !isError;

  return (
    <>
      {showLoader && (
        <LoaderWrapper>
          <LoadingScreen variant="circle" />
        </LoaderWrapper>
      )}
      {!showLoader && (
        <FormWrapper component="form" onSubmit={handleSubmit}>
          {showLabel && <LabelText variant="body2">Email</LabelText>}
          <TextField
            label={showLabel ? '' : 'Username'}
            name="username"
            value={formData.username}
            onChange={handleChange}
            margin="normal"
            type="text"
            color="success"
            fullWidth
            id="username"
            required
            error={errors.find((error) => error.type === 'email') ? true : false}
          />
          {errors
            .filter((el) => el.type === 'email')
            .map((error) => (
              <ErrorText variant="body2" key={error.message}>
                {error.message}
              </ErrorText>
            ))}
          {showLabel && (
            <LabelText variant="body2" style={{ marginTop: 24 }}>
              Password
            </LabelText>
          )}
          <TextField
            label={showLabel ? '' : 'Your made up password'}
            name="password"
            value={formData.password}
            onChange={handleChange}
            margin="normal"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            required
            error={errors.find((error) => error.type === 'password') ? true : false}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end" aria-label="toggle password visibility">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {errors
            .filter((el) => el.type === 'password')
            .map((error) => (
              <ErrorText variant="body2" key={error.message}>
                {error.message}
              </ErrorText>
            ))}
          <>
            {errorSendRecord && <ErrorText variant="body2">{errorSendRecord}</ErrorText>}
            {errorTrackEnrollment && <ErrorText variant="body2">An error occurred while creating the enrollment, please retry.</ErrorText>}
            {postSimulatorResultError && <ErrorText variant="body2">An error occurred while creating the result, please retry.</ErrorText>}
          </>

          <ButtonWrapper
            style={{
              justifyContent: backButton ? 'space-between' : 'flex-end',
              height: '100%',
              alignItems: 'flex-end',
              marginTop: showLabel ? 24 : 8,
            }}
          >
            {backButton && setCurrentStep && (
              <Button type="button" variant="outlined" color="primary" onClick={handleBack}>
                Back
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              style={{
                width: setCurrentStep ? 'fit-content' : '100%',
                height: showLabel ? 51 : 'auto',
              }}
            >
              {setCurrentStep ? 'Next' : 'Sign in'}
            </Button>
          </ButtonWrapper>
        </FormWrapper>
      )}
    </>
  );
};

export default FakeLoginForm;
